import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { login } from './AuthActions';
import RouterPaths from '../constants/RouterPaths';
import CommonButton from '../common/components/CommonButton';
import FormInputField, {
  FormInputFieldType,
} from '../common/components/formFields/FormInputField';
import validationSchema from './Login.schema';
import { CommonButtonVariants } from '../constants/CommonButtonVariants';
import logo from '../assets/img/logo.png';
import logoMobile from '../assets/img/icons/logo-mobile.svg';
import arrowBack from '../assets/img/icons/arrow_back.svg';
import authCar from '../assets/img/tsv-auth-car.png';
import { useMediaQuery } from '../common/hooks/useMediaQuery';
import AlertContainer from '../common/components/alert/AlertContainer';
import useIsAuthenticated from '../common/hooks/useIsAuthenticated';

const { DISABLED, PRIMARY_RED } = CommonButtonVariants;

const initialValues = {
  email: '',
  password: '',
};

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useIsAuthenticated();

  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleSubmit = ({ email, password }, { setSubmitting }) => {
    dispatch(login(email, password));
    setSubmitting(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleSignUpClick = () => {
    navigate(RouterPaths.REGISTER);
  };

  const authLogo = isMobile ? logoMobile : logo;

  return (
    <div className="auth__page">
      <div className="auth__container">
        <div className="auth__credentials-section">
          <div className="auth__header">
            <Link to={RouterPaths.HOMEPAGE} className="auth__logo-link">
              <img src={authLogo} alt="The Stock Vault" className="auth__logo" />
            </Link>
            <CommonButton
              className="auth__back-button"
              type="button"
              label="Back"
              variant={CommonButtonVariants.OUTLINED}
              icon={arrowBack}
              iconAlt="Back to homepage"
              iconLeft
              handleClick={handleBackClick}
            />
          </div>
          <div className="auth__form-wrapper">
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {() => (
                <Form className="auth__form">
                  <h3 className="auth__heading">Login to your account</h3>
                  <p className="auth__login-description">
                    Enter your login details to unlock the stock.
                  </p>
                  <div className="auth__login-error">
                    <AlertContainer position="custom" dismissable={false} />
                  </div>
                  <FormInputField
                    labelVariant="bordered"
                    inputClassName="auth__login-input-field"
                    wrapperClassName="auth__login-input-wrapper"
                    name="email"
                    type={FormInputFieldType.EMAIL}
                    label="Email"
                    required
                    aria-describedby="email-error"
                  />
                  <FormInputField
                    labelVariant="bordered"
                    inputClassName="auth__login-input-field"
                    wrapperClassName="auth__login-input-wrapper"
                    name="password"
                    type={FormInputFieldType.PASSWORD}
                    label="Password"
                    required
                    aria-describedby="password-error"
                    showPasswordIcon={true}
                  />
                  <div className="auth__form-options">
                    <label className="auth__checkbox-container">
                      <input
                        className="auth__checkbox-hidden"
                        type="checkbox"
                        name="remember"
                      />
                      <span className="auth__checkbox-custom-icon" />
                      <span className="auth__checkbox-label">Remember for 30 days</span>
                    </label>
                    {!isMobile && (
                      <Link
                        className="auth__forgot-password-link"
                        to={RouterPaths.FORGOT_PASSWORD}
                      >
                        Forgot your password?
                      </Link>
                    )}
                  </div>
                  <CommonButton
                    type="submit"
                    label="Login"
                    className="auth__login-button"
                    disabled={isLoading}
                    variant={isLoading ? DISABLED : PRIMARY_RED}
                  />
                  {isMobile && (
                    <Link
                      className="auth__forgot-password-link"
                      to={RouterPaths.FORGOT_PASSWORD}
                    >
                      Forgot your password?
                    </Link>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
